import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getMovieById, getRelated } from "@/utils/movies";
import { movies } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import EpisodeList from "@/pages/detail/components/EpisodeList";

const genreCategoryMap: Record<string, string> = {
  动作: "action",
  爱情: "romance",
  科幻: "scifi",
  悬疑: "suspense",
};

const regionCategoryMap: Record<string, string> = {
  中国: "china",
  美国: "hollywood",
  英国: "hollywood",
};

export default function Detail() {
  const { id = "" } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [episode, setEpisode] = useState(1);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setEpisode(movie?.currentEpisode || 1);
    setCopied(false);
  }, [id, movie]);

  const related = useMemo(() => {
    if (!movie) return [];
    const list = getRelated(movie, 8);
    if (list.length >= 4) return list.slice(0, 6);
    const fallback = movies.filter((m) => m.id !== movie.id).sort((a, b) => b.popularity - a.popularity);
    return [...list, ...fallback].filter((m, i, arr) => arr.findIndex((x) => x.id === m.id) === i).slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <div className="px-4 md:px-8 py-24 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500 mb-4">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="text-xl font-heading font-bold text-foreground-950">未找到该影视内容</h1>
        <p className="mt-2 text-sm font-body text-foreground-500">内容可能已下架或链接有误</p>
        <Link
          to="/"
          className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const fav = isFavorite(movie.id);
  const typeLabel = movie.type === "movie" ? "电影" : "剧集";

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const categoryChips: { label: string; to: string }[] = [];
  if (movie.region && regionCategoryMap[movie.region]) {
    categoryChips.push({ label: `${movie.region}影视`, to: `/category/${regionCategoryMap[movie.region]}` });
  }
  movie.genres.forEach((g) => {
    const key = genreCategoryMap[g];
    if (key) categoryChips.push({ label: `${g}片`, to: `/category/${key}` });
  });
  categoryChips.push({
    label: movie.type === "movie" ? "全部电影" : "全部电视剧",
    to: `/category/${movie.type}`,
  });

  const metaRows = [
    { label: "类型", value: movie.type === "movie" ? "电影" : "电视剧" },
    { label: "地区", value: movie.region },
    { label: "年份", value: `${movie.year}` },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join("、") },
    { label: "片长", value: movie.duration },
    { label: "题材", value: movie.genres.join(" / ") },
  ];

  return (
    <div className="w-full">
      <section className="relative w-full h-[360px] md:h-[460px] overflow-hidden bg-primary-950">
        <img
          src={movie.backdrop}
          alt={`《${movie.title}》剧照`}
          title={`${movie.title} 高清影视`}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/30"></div>

        <div className="absolute inset-x-0 bottom-0 px-4 md:px-8">
          <div className="flex items-end gap-4 md:gap-6">
            <div className="w-24 sm:w-32 md:w-40 shrink-0 aspect-[2/3] rounded-lg overflow-hidden border-2 border-background-50/20 -mb-8 md:-mb-12">
              <img
                src={movie.poster}
                alt={`《${movie.title}》海报`}
                title={`${movie.title} 高清影视`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="pb-4 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2.5 py-0.5 rounded-full bg-accent-500 text-primary-950 text-xs font-heading font-bold">
                  {typeLabel}
                </span>
                {movie.type === "tv" && movie.episodeCount && (
                  <span className="px-2.5 py-0.5 rounded-full bg-background-50/15 text-background-50 text-xs font-label backdrop-blur">
                    更新至 {movie.currentEpisode}/{movie.episodeCount} 集
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 leading-tight">
                {movie.title}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 text-sm font-body text-background-200">
                <span className="flex items-center gap-1 text-accent-400 font-heading font-semibold">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-sm"></i>
                  </span>
                  {movie.rating.toFixed(1)}
                </span>
                <span className="text-background-400">|</span>
                <span>{movie.year}</span>
                <span className="text-background-400">|</span>
                <span>{movie.region}</span>
                <span className="text-background-400">|</span>
                <span>{movie.genres.join(" / ")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 pt-14 md:pt-16">
        <Breadcrumb
          items={[
            { label: "影视分类", to: `/category/${movie.type}` },
            { label: movie.type === "movie" ? "电影" : "电视剧", to: `/category/${movie.type}` },
            { label: movie.title },
          ]}
        />
      </div>

      <div className="px-4 md:px-8 py-6 flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">剧情简介</h2>
            </div>
            <p className="text-sm md:text-base font-body leading-loose text-foreground-700">{movie.synopsis}</p>
          </div>

          {movie.type === "tv" && movie.episodeCount && (
            <div className="mb-8">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">剧集列表</h2>
                </div>
                <span className="text-xs font-body text-foreground-500">共 {movie.episodeCount} 集</span>
              </div>
              <EpisodeList total={movie.episodeCount} current={episode} onSelect={setEpisode} />
              <div className="mt-4 flex flex-wrap items-center gap-3 p-4 rounded-lg border border-background-200 bg-background-100">
                <span className="flex items-center gap-2 text-sm font-body text-foreground-700">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                    <i className="ri-play-circle-line text-lg"></i>
                  </span>
                  当前选中：第 {episode} 集
                </span>
                <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label">
                  播放功能为前端演示状态
                </span>
              </div>
            </div>
          )}

          {movie.type === "movie" && (
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">在线播放</h2>
              </div>
              <div className="p-4 rounded-lg border border-background-200 bg-background-100 flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-2 text-sm font-body text-foreground-700">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                    <i className="ri-play-circle-line text-lg"></i>
                  </span>
                  片长 {movie.duration}
                </span>
                <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label">
                  播放功能为前端演示状态
                </span>
              </div>
            </div>
          )}

          <div>
            <SectionHeading title="相关推荐" subtitle="根据同类型、同地区为你推荐" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-5">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </div>
        </div>

        <aside className="w-full lg:w-80 shrink-0">
          <div className="sticky top-20 flex flex-col gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`flex items-center justify-center gap-2 w-full py-3 rounded-md text-sm font-label font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    fav
                      ? "bg-accent-500 text-primary-950"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${fav ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`}></i>
                  </span>
                  {fav ? "已收藏" : "加入收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-md border border-background-300 text-sm font-label text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-share-line text-lg"></i>
                  </span>
                  {copied ? "链接已复制" : "分享"}
                </button>
              </div>
              <p className="mt-3 text-xs font-body text-foreground-400 leading-relaxed">
                收藏保存在本地浏览器，仅作前端演示，可随时取消。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base font-heading font-bold text-foreground-950 mb-4">影视资料</h2>
              <dl className="flex flex-col gap-3">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm font-body">
                    <dt className="w-14 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="text-foreground-800 leading-relaxed">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base font-heading font-bold text-foreground-950 mb-4">所属分类</h2>
              <div className="flex flex-wrap gap-2">
                {categoryChips.map((chip) => (
                  <Link
                    key={chip.label}
                    to={chip.to}
                    className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label hover:bg-primary-500 hover:text-background-50 transition-colors"
                  >
                    {chip.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}